// useGenerateLinks.ts
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios, { AxiosError } from 'axios'
import { NewDeal } from 'types'
import { updateDealTimelineData } from '../../components/Timeline/createTimelineData'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { useCreateTimeline } from '../aws/useCreateTimeline'

/** ---------- Types ---------- */

export type Recipient =
  | {
      id?: string
      email?: string
      first_name?: string
      last_name?: string
      name?: string
      panda_contact_id?: string
    }
  | null
  | undefined

type MaybeArray<T> = T | T[] | null | undefined

export type GenerateLinksVars = {
  contractId: string
  PK: string
  SK: string
  // Optional recipients; if you pass these we’ll normalize to a single array
  authorizedRecipient?: MaybeArray<Recipient>
  contactRecipient?: MaybeArray<Recipient>
  requestedBy?: Recipient
  extraRecipients?: MaybeArray<Recipient>
}

export type GenerateLinksResponse = {
  message: string
  links?: {
    contactDocumentLink?: string | null
    authorizedDocumentLink?: string | null
  }
}

type TimelineUser = any // replace with your real user type if available

/** ---------- Helpers ---------- */

const pandaAccessToken =
  import.meta.env.VITE_ENV === 'local'
    ? '44c682867cd90875b3ba53db7280c56e1a4d6068'
    : localStorage.getItem('panda_access_token') ?? ''

function pickOne<T>(val: MaybeArray<T>): T | null {
  if (!val) return null
  return Array.isArray(val) ? val[0] ?? null : val
}

function hasEmail(r: Recipient): r is Required<Pick<NonNullable<Recipient>, 'email'>> & NonNullable<Recipient> {
  return !!r && typeof r.email === 'string' && r.email.trim().length > 3 && r.email.includes('@')
}

function normalizeRecipients(vars: GenerateLinksVars) {
  const auth = pickOne(vars.authorizedRecipient)
  const contact = pickOne(vars.contactRecipient)
  const requested = vars.requestedBy ? [vars.requestedBy] : []
  const extras = Array.isArray(vars.extraRecipients)
    ? vars.extraRecipients
    : vars.extraRecipients
    ? [vars.extraRecipients]
    : []

  // build flat list
  const list = [auth, contact, ...requested, ...extras].filter(Boolean) as Recipient[]

  // validate & coerce to the minimal shape backend/PandaDoc needs
  const missing = list.filter(r => !hasEmail(r))
  if (missing.length) {
    const who = missing.map(m => m?.name ?? m?.id ?? '(unknown)').join(', ')
    throw new Error(`Missing email for: ${who}`)
  }

  const normalized = list.map(r => ({
    email: r!.email!.trim(),
    first_name: r?.first_name ?? r?.name?.split(' ')?.[0] ?? '',
    last_name: r?.last_name ?? r?.name?.split(' ')?.slice(1).join(' ') ?? '',
    panda_contact_id: r?.panda_contact_id,
    id: r?.id,
  }))

  return normalized
}

/** ---------- Raw request (used by the mutation) ---------- */
//@ts-ignore
async function requestGenerateLinks(vars: GenerateLinksVars): Promise<GenerateLinksResponse> {
  const { PK, SK, contractId } = vars
  if (!contractId || !PK || !SK) throw new Error('Missing required fields (contractId, PK, SK)')

  if (!pandaAccessToken) {
    throw new Error('No PandaDoc authentication token available')
  }

  const url = `${baseUrl}/aws/pandadoc/${contractId}/send`

  // Optional: include recipients if provided
  let recipients:
    | Array<{ email: string; first_name?: string; last_name?: string; id?: string; panda_contact_id?: string }>
    | undefined
  recipients = normalizeRecipients(vars) // throws if any missing email
  const { data } = await axios.post<GenerateLinksResponse>(
    url,
    { PK, SK, recipients }, // <= backend will forward this to PandaDoc
    {
      // IMPORTANT: do NOT prefix with "Bearer " here — the backend already adds it.
      headers: { Authorization: pandaAccessToken },
    }
  )
  return data
}

/** ---------- Hook ---------- */

export const useGenerateLinks = (
  newDeal: NewDeal,
  activeUser: TimelineUser
): UseMutationResult<GenerateLinksResponse, unknown, GenerateLinksVars> => {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const { mutate: createTimelineEntry } = useCreateTimeline()

  const timelineData = updateDealTimelineData(
    newDeal?.deal_uuid,
    activeUser,
    'Generate',
    'document',
    'Links Generated',
    'Links generated via PandaDoc API'
  )

  return useMutation<GenerateLinksResponse, unknown, GenerateLinksVars>({
    mutationFn: requestGenerateLinks,
    onSuccess: (data, variables) => {
      showSnackbar('Links generated successfully', 'success')
      createTimelineEntry(timelineData)

      // Merge generated links into item-level cache
      // Note: SEARCH_DATA_KEY is updated by the call-site via syncDealFields
      const links = data?.links
      if (links) {
        queryClient.setQueryData(['item', variables.PK, variables.SK], (old: any) => {
          if (!old) return old
          return {
            ...old,
            ...(links.authorizedDocumentLink !== undefined && { authorizedDocumentLink: links.authorizedDocumentLink }),
            ...(links.contactDocumentLink !== undefined && { contactDocumentLink: links.contactDocumentLink }),
          }
        })
      }

      // Invalidate org-level cache for fresh data on navigation
      queryClient.invalidateQueries({ queryKey: ['all_org_items', variables.PK] })
    },
    onError: error => {
      // surface a useful error
      const axiosErr = error as AxiosError<any>
      const serverDetail =
        axiosErr?.response?.data?.detailMessage ||
        axiosErr?.response?.data?.message ||
        axiosErr?.message ||
        'Unknown error'
      console.error('[useGenerateLinks] Error generating links:', serverDetail)
    },
  })
}

// import { useMutation } from '@tanstack/react-query'
// import axios from 'axios'
// import { baseUrl } from 'api/config'
// import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
// import { updateDealTimelineData } from '../../components/Timeline/createTimelineData'
// import { useCreateTimeline } from '../aws/useCreateTimeline'

// const pandaAccessToken =
//   import.meta.env.VITE_ENV === 'local'
//     ? '44c682867cd90875b3ba53db7280c56e1a4d6068'
//     : localStorage.getItem('panda_access_token')

// const generateLinks = async linkData => {
//   const { PK, SK, contractId } = linkData ?? {}
//   const url = `${baseUrl}/aws/pandadoc/${contractId}/send`
//   if (!pandaAccessToken) {
//     console.error('No authentication token available')
//     return
//   }
//   try {
//     const response = await axios.post(
//       url,
//       { PK, SK },
//       {
//         headers: { Authorization: ` ${pandaAccessToken}` },
//       }
//     )
//     console.log('Response generate links: ', response)
//     return response.data
//   } catch (error) {
//     console.error('Error generating links:', error)
//     throw error // Re-throw the error for useMutation's onError to catch
//   }
// }

// export const useGenerateLinks = (newDeal, activeUser) => {
//   const { showSnackbar } = useSnackbarContext()
//   const { mutate: createTimelineEntry } = useCreateTimeline()
//   const timelineData = updateDealTimelineData(
//     newDeal?.deal_uuid,
//     activeUser,
//     'Generate',
//     'document',
//     `Links Generated`,
//     `Links generated via PandaDoc API`
//   )
//   const mutation = useMutation({
//     // Directly pass the object to the mutation function
//     mutationFn: generateLinks,
//     onSuccess: () => {
//       showSnackbar('Links generated successfully', 'success')
//       console.log('Successfully generated links')
//       createTimelineEntry(timelineData)
//     },
//     onError: error => {
//       console.error('[useGenerateLinks] Error setting to draft:', error)
//     },
//   })

//   return mutation
// }
